import type { ComponentType, ReactNode } from 'react';

import type { Locale } from '@kilat/shared';

/**
 * Article titles and dates, generated from the MDX sources at build time.
 *
 * `loadHelpToc` used to build the table of contents by calling
 * `loadHelpArticle` for all fourteen slugs, which downloads fourteen compiled
 * MDX bodies in order to read fourteen titles. The manual index and the
 * per-article "related" footer both render a TOC, so every help page pulled the
 * whole manual: measured 2026-08-23, ~57 KB on the Malay pages and ~140 KB on
 * the Tamil ones, none of it read.
 *
 * The obvious fix does NOT work, and was measured before this one was written:
 * `import.meta.glob(..., { eager: true, import: 'meta' })` cannot tree-shake a
 * compiled MDX body out of its module, so every article got hoisted into the
 * SHARED chunk instead of its own lazy one and the help pages went from 640 KB
 * to 813 KB. A generated file references no MDX module at all, which is the
 * only thing that keeps the bodies out.
 *
 * `pnpm help:meta` regenerates it; `pnpm help:meta:check` fails the gate when it
 * drifts from the MDX, so a title here cannot silently disagree with its article.
 */
import { HELP_ARTICLE_META } from './article-meta.generated.js';

/**
 * Help-article registry — the single place that knows which manual articles
 * exist and how to load them (docs/features/public-site-and-manual.feature.md
 * §"Operator manual"). Articles are MDX under src/content/help/{locale}/{slug}.mdx,
 * lazy-loaded per route visit via import.meta.glob so the manual never weighs
 * down the marketing bundle.
 *
 * Locale resolution: try the visitor's locale, fall back to English. The
 * caller gets `localeUsed` back so the UI can say "showing English" honestly
 * instead of silently swapping languages (no-dead-ends rule).
 */

export const HELP_SLUGS = [
  'getting-started',
  'queue-board',
  'pos',
  'catalog',
  'branding',
  'loyalty',
  'promotions',
  'broadcasts',
  'notifications',
  'staff',
  'billing',
  'agent-referrals',
  'faq',
  'troubleshooting',
] as const;
export type HelpSlug = (typeof HELP_SLUGS)[number];

export function isHelpSlug(value: string): value is HelpSlug {
  return (HELP_SLUGS as readonly string[]).includes(value);
}

export interface HelpArticleMeta {
  readonly title: string;
  readonly updatedAt: string;
  readonly related: readonly string[];
}

export type HelpMdxComponents = Record<
  string,
  ComponentType<{ readonly children?: ReactNode; readonly href?: string }>
>;

export type HelpMdxBody = ComponentType<{ readonly components?: HelpMdxComponents }>;

export interface LoadedHelpArticle {
  readonly slug: HelpSlug;
  readonly meta: HelpArticleMeta;
  readonly Body: HelpMdxBody;
  /** Locale the content actually rendered in (en when the translation is missing). */
  readonly localeUsed: Locale;
}

export interface HelpTocEntry {
  readonly slug: HelpSlug;
  readonly meta: HelpArticleMeta;
  readonly localeUsed: Locale;
}

interface HelpMdxModule {
  readonly default: HelpMdxBody;
  readonly meta: HelpArticleMeta;
}

function isHelpArticleMeta(value: unknown): value is HelpArticleMeta {
  if (typeof value !== 'object' || value === null) return false;
  const m = value as Record<string, unknown>;
  return (
    typeof m['title'] === 'string' &&
    typeof m['updatedAt'] === 'string' &&
    Array.isArray(m['related']) &&
    m['related'].every((r) => typeof r === 'string')
  );
}

function isHelpMdxModule(mod: unknown): mod is HelpMdxModule {
  if (typeof mod !== 'object' || mod === null) return false;
  const m = mod as Record<string, unknown>;
  return typeof m['default'] === 'function' && isHelpArticleMeta(m['meta']);
}

// Vite resolves this at build time into one lazy chunk per article.
const articleModules = import.meta.glob('../../content/help/*/*.mdx');

function moduleKey(locale: Locale, slug: HelpSlug): string {
  return `../../content/help/${locale}/${slug}.mdx`;
}

export async function loadHelpArticle(locale: Locale, slug: HelpSlug): Promise<LoadedHelpArticle> {
  const attempts: readonly Locale[] = locale === 'en' ? ['en'] : [locale, 'en'];
  for (const candidate of attempts) {
    const loader = articleModules[moduleKey(candidate, slug)];
    if (!loader) continue;
    const mod = await loader();
    if (isHelpMdxModule(mod)) {
      return { slug, meta: mod.meta, Body: mod.default, localeUsed: candidate };
    }
  }
  throw new Error(`Help article "${slug}" is missing for locale "${locale}" (and en fallback).`);
}

/**
 * Titles and dates for every article, in the visitor's locale where it exists.
 *
 * Synchronous: the metadata is inlined at build time, so there is nothing to
 * await and no loading state for a caller to render. Falls back to English per
 * article, and reports `localeUsed` so the UI can say so honestly.
 */
export function helpToc(locale: Locale): readonly HelpTocEntry[] {
  const attempts: readonly Locale[] = locale === 'en' ? ['en'] : [locale, 'en'];
  return HELP_SLUGS.map((slug) => {
    for (const candidate of attempts) {
      const meta = HELP_ARTICLE_META[candidate]?.[slug];
      if (isHelpArticleMeta(meta)) return { slug, meta, localeUsed: candidate };
    }
    throw new Error(`Help article "${slug}" is missing for locale "${locale}" (and en fallback).`);
  });
}
